import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiBase';

const SurveyPage = () => {
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [guestToken, setGuestToken] = useState('');

  useEffect(() => {
    // Prefill guest token if passed in query
    const tokenFromQuery = searchParams.get('token');
    if (tokenFromQuery) setGuestToken(tokenFromQuery);
    loadSurvey();
  }, [surveyId, searchParams]);

  const loadSurvey = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/guest-surveys/${surveyId}`);
      if (!response.ok) {
        throw new Error('Survey not found or not available');
      }
      
  const data = await response.json();
  setSurvey(data.survey);
  // API returns questions nested under survey
  setQuestions(data.survey?.questions || data.questions || []);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (questionId, value) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const validateResponses = () => {
    const requiredQuestions = questions.filter(q => q.is_required);
    const missingRequired = requiredQuestions.filter(q => 
      !responses[q.id] || (Array.isArray(responses[q.id]) && responses[q.id].length === 0)
    );
    
    if (missingRequired.length > 0) {
      setError(`Please answer all required questions: ${missingRequired.map(q => q.question_text).join(', ')}`);
      return false;
    }
    
    return true;
  };

  const submitSurvey = async () => {
    if (!validateResponses()) return;
    
    setSubmitting(true);
    setError('');

    try {
      const payload = {
        guest_token: guestToken || null,
        answers: Object.entries(responses).map(([questionId, answer]) => ({
          question_id: parseInt(questionId),
          answer_data: typeof answer === 'string' ? answer : JSON.stringify(answer)
        }))
      };

      const response = await fetch(`${API_BASE_URL}/guest-surveys/${surveyId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to submit survey');
      }

      setSubmitted(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    const value = responses[question.id] || '';
    
    switch (question.question_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Your answer..."
            className="form-input"
          />
        );
        
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="Your answer..."
            rows="4"
            className="form-input"
          />
        );
        
      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            placeholder="your.email@example.com"
            className="form-input"
          />
        );
        
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="form-input"
          />
        );
        
      case 'radio':
        return (
          <div className="radio-group">
            {(question.options || []).map((option, index) => (
              <label key={index} className="radio-option">
                <input
                  type="radio"
                  name={`question_${question.id}`}
                  value={option}
                  checked={value === option}
                  onChange={(e) => handleResponseChange(question.id, e.target.value)}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
        
      case 'checkbox':
        const checkboxValues = Array.isArray(value) ? value : [];
        return (
          <div className="checkbox-group">
            {(question.options || []).map((option, index) => (
              <label key={index} className="checkbox-option">
                <input
                  type="checkbox"
                  value={option}
                  checked={checkboxValues.includes(option)}
                  onChange={(e) => {
                    const newValues = e.target.checked
                      ? [...checkboxValues, option]
                      : checkboxValues.filter(v => v !== option);
                    handleResponseChange(question.id, newValues);
                  }}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
        );
        
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="form-input"
          >
            <option value="">Please select...</option>
            {(question.options || []).map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
        
      case 'rating':
        const maxRating = question.validation_rules?.max_rating || 5;
        return (
          <div className="rating-group">
            {[...Array(maxRating)].map((_, index) => (
              <button
                key={index}
                type="button"
                className={`rating-button ${parseInt(value) > index ? 'selected' : ''}`}
                onClick={() => handleResponseChange(question.id, (index + 1).toString())}
              >
                ⭐
              </button>
            ))}
            <span className="rating-label">
              {value ? `${value}/${maxRating}` : 'Not rated'}
            </span>
          </div>
        );
        
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleResponseChange(question.id, e.target.value)}
            className="form-input"
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="survey-container">
        <div className="loading">Loading survey...</div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="survey-container">
        <div className="error-message">
          <h2>Survey Not Available</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="survey-container">
        <div className="success-message">
          <h2>Thank You! 🎉</h2>
          <p>Your survey response has been submitted successfully.</p>
          <p>We appreciate your feedback!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="survey-container">
      <div className="survey-header">
        <h1>{survey.title}</h1>
        {survey.description && (
          <p className="survey-description">{survey.description}</p>
        )}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); submitSurvey(); }} className="survey-form">
        {/* Optional guest token field for linking responses */}
        <div className="form-group">
          <label>
            Guest Code (optional)
            <span className="help-text">If you have a guest code, enter it here to link your response</span>
          </label>
          <input
            type="text"
            value={guestToken}
            onChange={(e) => setGuestToken(e.target.value)}
            placeholder="Enter your guest code (optional)"
            className="form-input"
          />
        </div>

        {questions.map((question, index) => (
          <div key={question.id} className="form-group">
            <label>
              {index + 1}. {question.question_text}
              {question.is_required && <span className="required">*</span>}
            </label>
            
            {question.help_text && (
              <p className="help-text">{question.help_text}</p>
            )}
            
            {renderQuestion(question)}
          </div>
        ))}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : 'Submit Survey'}
          </button>
        </div>
      </form>

      <style jsx>{`
        .survey-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }

        .survey-header {
          text-align: center;
          margin-bottom: 40px;
          padding-bottom: 20px;
          border-bottom: 1px solid #eee;
        }

        .survey-header h1 {
          color: #333;
          margin-bottom: 10px;
        }

        .survey-description {
          color: #666;
          font-size: 16px;
          line-height: 1.5;
        }

        .survey-form {
          max-width: 600px;
          margin: 0 auto;
        }

        .form-group {
          margin-bottom: 30px;
        }

        .form-group label {
          display: block;
          font-weight: bold;
          margin-bottom: 8px;
          color: #333;
        }

        .required {
          color: #dc3545;
          margin-left: 4px;
        }

        .help-text {
          color: #666;
          font-size: 14px;
          font-weight: normal;
          margin-top: 4px;
          margin-bottom: 8px;
        }

        .form-input {
          width: 100%;
          padding: 12px;
          border: 2px solid #ddd;
          border-radius: 6px;
          font-size: 16px;
          transition: border-color 0.3s;
        }

        .form-input:focus {
          border-color: #007bff;
          outline: none;
        }

        .radio-group,
        .checkbox-group {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .radio-option,
        .checkbox-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          font-weight: normal;
        }

        .radio-option input,
        .checkbox-option input {
          width: auto;
        }

        .rating-group {
          display: flex;
          align-items: center;
          gap: 5px;
        }

        .rating-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          opacity: 0.3;
          transition: opacity 0.2s;
        }

        .rating-button.selected {
          opacity: 1;
        }

        .rating-button:hover {
          opacity: 0.7;
        }

        .rating-label {
          margin-left: 10px;
          color: #666;
          font-size: 14px;
        }

        .form-actions {
          text-align: center;
          margin-top: 40px;
          padding-top: 20px;
          border-top: 1px solid #eee;
        }

        .submit-button {
          background: #007bff;
          color: white;
          border: none;
          padding: 15px 30px;
          border-radius: 6px;
          font-size: 16px;
          font-weight: bold;
          cursor: pointer;
          transition: background-color 0.3s;
        }

        .submit-button:hover:not(:disabled) {
          background: #0056b3;
        }

        .submit-button:disabled {
          background: #ccc;
          cursor: not-allowed;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 15px;
          border-radius: 6px;
          margin: 20px 0;
          text-align: center;
        }

        .success-message {
          text-align: center;
          padding: 40px;
          color: #155724;
        }

        .success-message h2 {
          color: #155724;
          margin-bottom: 20px;
        }

        .loading {
          text-align: center;
          padding: 50px;
          color: #666;
          font-size: 18px;
        }
      `}</style>
    </div>
  );
};

export default SurveyPage;
